import React from 'react';

const MarketingSpecialistResumePreview = ({ componentRef, resumeData = {} }) => {
  // Process highlighting markers from AI response
  const {
    full_name,
    email,
    phone,
    location,
    websites,
    projects,
    professional_summary,
    relevant_experience,
    education,
    certifications,
    achievements,
    languages,
    interests,
    publications,
    volunteer_work,
    additional_info,
    skills = {},
    key_skills = [],
    technical_skills = [],
    contact = {}
  } = resumeData;

  // For marketing-specialist, skills is structured as { "Digital Marketing": [], "Tools": [] }
  const digitalMarketingSkills = skills["Digital Marketing"] || [];
  const toolsSkills = skills["Tools"] || [];

  // Ensure all skills arrays are properly initialized
  const safeDigitalMarketingSkills = Array.isArray(digitalMarketingSkills) ? digitalMarketingSkills : [];
  const safeToolsSkills = Array.isArray(toolsSkills) ? toolsSkills : [];
  const safeKeySkills = Array.isArray(key_skills) ? key_skills : [];
  const safeTechnicalSkills = Array.isArray(technical_skills) ? technical_skills : [];

  const isTemplateResume = resumeData && resumeData.isTemplate;
  const trimText = (value) => (typeof value === 'string' ? value.trim() : '');
  const pickDisplay = (value, fallback = '') => {
    const trimmed = trimText(value);
    if (trimmed) return trimmed;
    return isTemplateResume ? fallback : '';
  };

  const displayFullName = pickDisplay(full_name, 'JASMINE CLEDANOR');
  const displayEmail = pickDisplay(email, 'jasnor223@gmail.com');
  const displayPhone = pickDisplay(phone, '239-206-0560');
  const displayLocation = pickDisplay(location, 'Connecticut');
  const personalWebsite = trimText(resumeData.personal_website);
  const portfolioLink = trimText(resumeData.portfolio);
  const websiteLinks = Array.isArray(websites) ? websites.map(trimText).filter(Boolean) : [];
  const defaultSummaryText = 'Creative and passionate graduate with a talent for blending digital storytelling with influential content to deliver marketing campaigns that drive engagement and growth across social channels. Utilize complex analytics and data to identify audience expectations and build messaging that communicates value and fosters meaningful connections. Collaborative relationship builder and team player seeking an opportunity to blend academic knowledge with professional experience to deliver digital and social media goals while enhancing brand awareness.';
  const summaryContent = pickDisplay(professional_summary, defaultSummaryText);
  const shouldRenderSummary = Boolean(summaryContent);

  const cleanArray = (value) => {
    if (Array.isArray(value)) return value.map(trimText).filter(Boolean);
    if (typeof value === 'string') return value.split(/\r?\n|[•●▪■\-–;]+/).map(trimText).filter(Boolean);
    return [];
  };

  const formatDateRange = (item = {}) => {
    const start = trimText(item.start_date || item.start_year || '');
    const end = trimText(item.end_date || item.end_year || '');
    if (start && end) return `${start} – ${end}`;
    return start || end || '';
  };

  const experienceList = Array.isArray(relevant_experience) ? relevant_experience : [];
  const additionalExperienceList = Array.isArray(resumeData.additional_experience) ? resumeData.additional_experience : [];
  const educationList = Array.isArray(education) ? education : [];
  const achievementsList = Array.isArray(achievements) ? achievements.map(trimText).filter(Boolean) : [];
  const certificationsList = Array.isArray(certifications) ? certifications : [];
  const projectsList = Array.isArray(projects) ? projects : [];
  const languagesList = Array.isArray(languages) ? languages : [];
  const interestsList = Array.isArray(interests) ? interests.map(trimText).filter(Boolean) : [];
  const publicationsList = Array.isArray(publications) ? publications.map(trimText).filter(Boolean) : [];
  const volunteerList = Array.isArray(volunteer_work) ? volunteer_work.map(trimText).filter(Boolean) : [];
  const additionalInfoList = Array.isArray(additional_info) ? additional_info.map(trimText).filter(Boolean) : [];

  const socialLinks = [
    { label: 'GitHub', href: trimText(resumeData.github) },
    { label: 'LinkedIn', href: trimText(resumeData.linkedin) },
    { label: 'HackerRank', href: trimText(resumeData.hackerrank), icon: '/hakerranklogo.jpeg' },
    { label: 'LeetCode', href: trimText(resumeData.leetcode), icon: '/leetcodelogo.jpeg' },
  ].filter((link) => link.href);

  const contactParts = [];
  if (displayEmail) contactParts.push(`Email: ${displayEmail}`);
  if (displayPhone) contactParts.push(`Phone: ${displayPhone}`);
  if (displayLocation) contactParts.push(`Location: ${displayLocation}`);

  const websiteList = [personalWebsite, portfolioLink, ...websiteLinks].filter(Boolean);

  // Enhanced logic to detect when to show user data
  const shouldShowUserData = resumeData && (
      !resumeData.isTemplate || 
      resumeData.full_name || 
      resumeData.email || 
      resumeData.phone || 
      resumeData.location ||
      resumeData.websites ||
      resumeData.professional_summary ||
      resumeData.projects ||
      resumeData.education ||
      resumeData.relevant_experience ||
      resumeData.skills ||
      resumeData.key_skills ||
      resumeData.achievements ||
      resumeData.certifications
  );

  // Handle both template and uploaded resume data
  const displaySkills = safeKeySkills.length > 0 || 
    (safeDigitalMarketingSkills.length > 0 || safeToolsSkills.length > 0);

  // IMPROVED CONTENT FLOW LOGIC - Better page break decisions
  const calculatePageBreakStrategy = () => {
    let estimatedHeight = 0;
    const A4_HEIGHT_ESTIMATE = 950; // Approximate pixels for A4 in print
    const HEADER_HEIGHT = 100;
    const SECTION_HEADER_HEIGHT = 30;
    const CONTENT_LINE_HEIGHT = 20;
    
    // Header
    estimatedHeight += HEADER_HEIGHT;
    
    // Professional Summary
    if (professional_summary) {
      estimatedHeight += SECTION_HEADER_HEIGHT + CONTENT_LINE_HEIGHT * 2;
    }
    
    // Skills
    if (displaySkills) {
      estimatedHeight += SECTION_HEADER_HEIGHT + CONTENT_LINE_HEIGHT * 2;
    }
    
    // Experience
    if (relevant_experience?.length) {
      estimatedHeight += SECTION_HEADER_HEIGHT;
      relevant_experience.forEach(exp => {
        estimatedHeight += CONTENT_LINE_HEIGHT * 2; // Header
        estimatedHeight += CONTENT_LINE_HEIGHT * (exp.description_points?.length || 2);
      });
    }
    
    // Additional Experience
    if (resumeData.additional_experience?.length) {
      estimatedHeight += SECTION_HEADER_HEIGHT;
      estimatedHeight += CONTENT_LINE_HEIGHT * resumeData.additional_experience.length * 2;
    }
    
    // Education height estimate
    let educationHeight = 0;
    if (education?.length) {
      educationHeight += SECTION_HEADER_HEIGHT;
      education.forEach(edu => {
        educationHeight += CONTENT_LINE_HEIGHT * 3; // Title, location, details
      });
    }
    
    // Achievements height
    let achievementsHeight = 0;
    if (achievements?.length) {
      achievementsHeight += SECTION_HEADER_HEIGHT + (achievements.length * CONTENT_LINE_HEIGHT);
    }
    
    // Certifications height
    let certificationsHeight = 0;
    if (Array.isArray(certifications) && certifications.length > 0) {
      certificationsHeight += SECTION_HEADER_HEIGHT + Math.ceil(certifications.length / 2) * CONTENT_LINE_HEIGHT;
    }
    
    // Project height estimate
    let projectHeight = 0;
    if (projects?.length) {
      projectHeight += SECTION_HEADER_HEIGHT;
      projects.forEach(proj => {
        projectHeight += CONTENT_LINE_HEIGHT * 2; // Header
        if (proj.tech_stack) projectHeight += CONTENT_LINE_HEIGHT;
        if (Array.isArray(proj.description)) {
          projectHeight += proj.description.length * CONTENT_LINE_HEIGHT;
        } else {
          projectHeight += CONTENT_LINE_HEIGHT * 2;
        }
        if (proj.github_link) projectHeight += CONTENT_LINE_HEIGHT;
      });
    }
    
    const firstPageContent = estimatedHeight + educationHeight + achievementsHeight + certificationsHeight;
    const shouldProjectsOverflow = firstPageContent + projectHeight > A4_HEIGHT_ESTIMATE * 1.2;
    
    return {
      shouldProjectsOverflow,
      firstPageContent,
      projectHeight,
      educationHeight,
      achievementsHeight,
      certificationsHeight
    };
  };

  const pageStrategy = calculatePageBreakStrategy();
  const showProjectsOnFirstPage = projects && projects.length > 0 && !pageStrategy.shouldProjectsOverflow;
  const showProjectsOnSecondPage = projects && projects.length > 0 && pageStrategy.shouldProjectsOverflow;

  return (
    <div id="section-to-print" className="resume-preview">
      {/* PAGE 1 - Main Content */}
      <div className="resume-paper professional-template" ref={componentRef} data-page="1">
        <header className="professional-header">
          {displayFullName && (
            <h1 className="professional-name">
              {displayFullName}
            </h1>
          )}
          {contactParts.length > 0 && (
            <p className="professional-contact-info">
              {contactParts.join(' | ')}
            </p>
          )}

          {socialLinks.length > 0 && (
            <p className="professional-contact-info social-links">
              {socialLinks.map((link, index) => (
                <React.Fragment key={link.label}>
                  {index > 0 && <span className="social-separator">|</span>}
                  {link.icon ? (
                    <span className="social-icon">
                      <img src={link.icon} alt={link.label} className="social-icon-img" />
                    </span>
                  ) : (
                    <span className="social-icon">🔗</span>
                  )}
                  <a href={link.href} target="_blank" rel="noreferrer" className="social-link">
                    {link.label}
                  </a>
                </React.Fragment>
              ))}
            </p>
          )}

          {websiteList.length > 0 && (
            <p className="professional-contact-info websites-links">
              {websiteList.map((site, index) => (
                <React.Fragment key={`${site}-${index}`}>
                  {index > 0 && <span className="social-separator">|</span>}
                  <a href={site} target="_blank" rel="noreferrer">
                    {site.replace(/^https?:\/\//, '').replace(/^www\./, '')}
                  </a>
                </React.Fragment>
              ))}
            </p>
          )}
        </header>

        {/* Summary - Section 2 */}
        {shouldRenderSummary && (
          <section className="professional-section">
            <h2 className="professional-section-title">SUMMARY</h2>
            <p className="professional-section-content">{summaryContent}</p>
          </section>
        )}

        {/* Education - Section 3 */}
        {educationList.length > 0 && (
          <section className="professional-section keep-together">
            <h2 className="professional-section-title">EDUCATION</h2>
            {educationList.map((edu, i) => {
              const degreeText = trimText(edu.degree);
              const fieldText = trimText(edu.field_of_study);
              const instituteText = trimText(edu.institute || edu.institution);
              const locationText = trimText(edu.location);
              const dateRange = formatDateRange(edu) || trimText(edu.graduation_date);
              const cgpaText = trimText(edu.cgpa || edu.gpa);
              const coursework = cleanArray(edu.coursework || edu.key_coursework);
              const awards = trimText(edu.awards_honors);
              const extracurricular = trimText(edu.extracurricular);
              const certificate = trimText(edu.certificate);
              const minorText = trimText(edu.minor);

              return (
                <div key={i} className="professional-education-item keep-together">
                  <div className="professional-education-header">
                    <div className="professional-experience-title">
                      {[degreeText, fieldText && `in ${fieldText}`].filter(Boolean).join(' ')}
                    </div>
                    {dateRange && (
                      <div className="professional-experience-dates">{dateRange}</div>
                    )}
                  </div>
                  <div className="education-details">
                    {[instituteText, locationText].filter(Boolean).join(', ')}
                    {cgpaText && <><br />CGPA: {cgpaText}</>}
                    {coursework.length > 0 && <><br />Relevant Coursework: {coursework.join(', ')}</>}
                    {minorText && <><br />Minor: {minorText}</>}
                    {awards && <><br />Awards &amp; Honors: {awards}</>}
                    {extracurricular && <><br />Extracurricular Activities: {extracurricular}</>}
                    {certificate && <><br />Certificate: {certificate}</>}
                  </div>
                </div>
              );
            })}
          </section>
        )}

        {/* Experience - Section 4 */}
        {experienceList.length > 0 && (
          <section className="professional-section">
            <h2 className="professional-section-title">EXPERIENCE</h2>
            {experienceList.map((exp, i) => {
              const titleText = trimText(exp.position);
              const companyText = trimText(exp.company);
              const locationText = trimText(exp.location);
              const dateRange = formatDateRange(exp);
              const points = cleanArray(exp.description_points || exp.responsibilities || exp.duties || exp.summary || exp.description);
              return (
                <div key={i} className="professional-experience-item">
                  {(titleText || companyText || locationText) && (
                    <div className="professional-experience-header">
                      <div className="professional-experience-title">
                        {[titleText, companyText].filter(Boolean).join(' | ')}
                        {locationText && `, ${locationText}`}
                      </div>
                      {dateRange && (
                        <div className="professional-experience-dates">{dateRange}</div>
                      )}
                    </div>
                  )}
                  {points.length > 0 && (
                    <ul className="professional-experience-points">
                      {points.map((pt, j) => (
                        <li key={j}>{pt}</li>
                      ))}
                    </ul>
                  )}
                </div>
              );
            })}
          </section>
        )}

        {/* Additional Experience */}
        {additionalExperienceList.length > 0 && (
          <section className="professional-section">
            <h2 className="professional-section-title">ADDITIONAL EXPERIENCE</h2>
            {additionalExperienceList.map((exp, i) => {
              const titleText = trimText(exp.position);
              const companyText = trimText(exp.company);
              const locationText = trimText(exp.location);
              const dateRange = formatDateRange(exp);
              if (!titleText && !companyText && !locationText && !dateRange) {
                return null;
              }
              return (
                <div key={i} className="professional-experience-item">
                  <div className="professional-experience-header">
                    <div className="professional-experience-title">
                      {[titleText, companyText].filter(Boolean).join(' | ')}
                      {locationText && `, ${locationText}`}
                    </div>
                    {dateRange && <div className="professional-experience-dates">{dateRange}</div>}
                  </div>
                </div>
              );
            })}
          </section>
        )}

        {/* Professional Skills - Section 5 */}
        {safeKeySkills.length > 0 && (
          <section className="professional-section">
            <h2 className="professional-section-title">PROFESSIONAL SKILLS</h2>
            <div className="jasmine-skills-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
              <div className="jasmine-skills-column">
                <ul className="jasmine-skills-list" style={{ listStyle: 'disc', paddingLeft: '20px', margin: 0 }}>
                  {safeKeySkills.slice(0, Math.ceil(safeKeySkills.length / 2)).map((skill, index) => (
                      <li key={index}>{skill}</li>
                  ))}
                </ul>
              </div>
              <div className="jasmine-skills-column">
                <ul className="jasmine-skills-list" style={{ listStyle: 'disc', paddingLeft: '20px', margin: 0 }}>
                  {safeKeySkills.slice(Math.ceil(safeKeySkills.length / 2)).map((skill, index) => (
                      <li key={index}>{skill}</li>
                  ))}
                </ul>
              </div>
            </div>
          </section>
        )}

        {/* Technical Skills - Section 6 */}
        {safeTechnicalSkills.length > 0 && (
          <section className="professional-section">
            <h2 className="professional-section-title">TECHNICAL SKILLS</h2>
            <div className="jasmine-tech-skills-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '15px' }}>
              {Array.from({ length: 4 }, (_, colIndex) => (
                <div key={colIndex} className="jasmine-tech-column">
                  <ul className="jasmine-tech-list" style={{ listStyle: 'disc', paddingLeft: '20px', margin: 0 }}>
                    {safeTechnicalSkills
                      .filter((_, index) => index % 4 === colIndex)
                      .map((skill, index) => (
                        <li key={index}>{skill}</li>
                      ))}
                </ul>
              </div>
              ))}
              </div>
          </section>
        )}

        {achievementsList.length > 0 && (
          <section className="professional-section">
            <h2 className="professional-section-title">ACHIEVEMENTS</h2>
            <ul className="professional-achievements-list">
              {achievementsList.map((a, i) => <li key={i}>{a}</li>)}
                </ul>
          </section>
        )}

        {certificationsList.length > 0 && (
          <section className="professional-section">
            <h2 className="professional-section-title">CERTIFICATIONS</h2>
            <ul
              className="certifications-grid-list"
              style={{ 
                listStyleType: 'disc', 
                paddingLeft: '20px', 
                margin: 0,
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
                gap: '8px'
              }}
            >
              {certificationsList.map((c, i) => {
                const label =
                  typeof c === 'string'
                    ? trimText(c)
                    : [trimText(c?.name || c?.title || c?.certification), trimText(c?.provider), trimText(c?.year) && `(${trimText(c?.year)})`]
                        .filter(Boolean)
                        .join(' — ');
                if (!label) return null;
                return <li key={i} className="certification-item">{label}</li>;
              })}
                </ul>
          </section>
        )}

        {projectsList.length > 0 && (
          <section className="professional-section">
            <h2 className="professional-section-title">PROJECTS</h2>
            {projectsList.map((proj, i) => {
              const titleText = trimText(proj.title || proj.name);
              const githubLink = trimText(proj.github_link);
              const yearText = trimText(proj.year);
              const techItems = Array.isArray(proj.tech_stack || proj.technologies)
                ? (proj.tech_stack || proj.technologies).map(trimText).filter(Boolean)
                : cleanArray(proj.tech_stack || proj.technologies);
              const descriptionItems = Array.isArray(proj.description)
                ? proj.description.map(trimText).filter(Boolean)
                : cleanArray(proj.description);

              return (
                <div key={i} className="professional-project-item">
                  {(titleText || githubLink || yearText) && (
                    <div className="professional-project-header">
                      <div className="professional-project-title-container">
                        {titleText && <h3 className="professional-project-title">{titleText}</h3>}
                        {githubLink && (
                          <a href={githubLink} target="_blank" rel="noreferrer" className="professional-project-github-link">
                            GitHub
                          </a>
                        )}
                      </div>
                      {yearText && (
                        <div className="professional-project-year">({yearText})</div>
                      )}
                    </div>
                  )}
                  {techItems.length > 0 && (
                    <p className="professional-project-tech">
                      <em>Tech Stack:</em> {techItems.join(', ')}
                    </p>
                  )}
                  {descriptionItems.length > 0 && (
                    <ul className="professional-project-points">
                      {descriptionItems.map((d, j) => (
                        <li key={j}>{d}</li>
                      ))}
                    </ul>
                  )}
                </div>
              );
            })}
          </section>
        )}

        {languagesList.length > 0 && (
          <section className="professional-section">
            <h2 className="professional-section-title">LANGUAGES</h2>
            <ul className="professional-achievements-list">
              {languagesList.map((lang, i) => {
                if (typeof lang === 'string') {
                  return <li key={i}>{trimText(lang)}</li>;
                }
                // ResumeEditor provides both 'language' and 'name' fields
                const languageName = trimText(lang.language || lang.name);
                const proficiencyLevel = trimText(lang.proficiency);
                if (!languageName) return null;
                return (
                  <li key={i}>
                    {languageName}
                    {proficiencyLevel ? ` (${proficiencyLevel})` : ''}
                  </li>
                );
              })}
            </ul>
          </section>
        )}

        {interestsList.length > 0 && (
          <section className="professional-section">
            <h2 className="professional-section-title">INTERESTS</h2>
            <p style={{ fontSize: '10pt', margin: 0 }}>{interestsList.join(', ')}</p>
          </section>
        )}

        {publicationsList.length > 0 && (
          <section className="professional-section">
            <h2 className="professional-section-title">PUBLICATIONS</h2>
            <ul className="professional-achievements-list">
              {publicationsList.map((pub, i) => (
                <li key={i}>{pub}</li>
              ))}
            </ul>
          </section>
        )}

        {volunteerList.length > 0 && (
          <section className="professional-section">
            <h2 className="professional-section-title">VOLUNTEER WORK</h2>
            <ul className="professional-achievements-list">
              {volunteerList.map((vol, i) => (
                <li key={i}>{vol}</li>
              ))}
            </ul>
          </section>
        )}

        {additionalInfoList.length > 0 && (
          <section className="professional-section">
            <h2 className="professional-section-title">ADDITIONAL INFORMATION</h2>
            <ul className="professional-achievements-list">
              {additionalInfoList.map((info, i) => (
                <li key={i}>{info}</li>
              ))}
            </ul>
            </section>
        )}

          </div>

      {/* CSS for better print layout */}
      <style jsx>{`
        .resume-paper {
          width: var(--a4-width) !important;
          max-width: var(--a4-width) !important;
          min-height: var(--a4-height) !important;
          margin: 0 auto !important;
          padding: var(--resume-padding-vertical) var(--resume-padding-horizontal) !important;
          box-sizing: border-box !important;
          background: white !important;
          font-size: 11pt !important;
          line-height: 1.4 !important;
        }
        
        .keep-together {
          page-break-inside: avoid !important;
          break-inside: avoid !important;
        }
        
        .professional-education-item {
          page-break-inside: avoid !important;
          break-inside: avoid !important;
          margin-bottom: 12px !important;
        }
        
        .page-break-before {
          page-break-before: always !important;
          break-before: page !important;
        }
        
        .professional-section {
          margin-bottom: 16px !important;
        }
        
        .professional-section-title {
          font-size: 12pt !important;
          font-weight: bold !important;
          margin-bottom: 8px !important;
          padding-bottom: 2px !important;
          border-bottom: 1px solid #000 !important;
        }
        
        .certifications-grid-list {
          column-count: 2 !important;
          column-gap: 20px !important;
          column-fill: balance !important;
        }
        
        .certification-item {
          break-inside: avoid !important;
          page-break-inside: avoid !important;
          margin-bottom: 4px !important;
          font-size: 10pt !important;
        }
        
        /* Social Links Styling */
        .social-links {
         
          align-items: center !important;
          flex-wrap: wrap !important;
          gap: 0 !important;
        }
        
        .social-icon {
          font-size: 14px !important;
          margin-right: 0px !important;
          color: #000000 !important;
          display: inline-flex !important;
          align-items: center !important;
          vertical-align: middle !important;
        }
        
        .social-icon svg {
          width: 14px !important;
          height: 14px !important;
        }
        
        .social-icon-img {
          width: 18px !important;
          height: 18px !important;
          object-fit: contain !important;
          vertical-align: middle !important;
          display: inline-block !important;
        }
        
        .social-separator {
          margin: 0 8px !important;
          color: #000000 !important;
          font-weight: normal !important;
        }
        
        .social-link {
          color: #000000 !important;
          text-decoration: underline !important;
          text-decoration-color: #000000 !important;
          font-weight: normal !important;
          margin-right: 0 !important;
        }
        
        .social-link:hover {
          color: #000000 !important;
          text-decoration: underline !important;
        }
        
        /* Project GitHub Link Styling */
        .professional-project-title-container {
          display: flex !important;
          align-items: center !important;
          gap: 12px !important;
        }
        
        .professional-project-github-link {
          color: #000000 !important;
          text-decoration: underline !important;
          font-size: 12pt !important;
          font-weight: normal !important;
          margin-left: 8px !important;
        }
        
        .professional-project-github-link:hover {
          color: #000000 !important;
          text-decoration: underline !important;
        }
        
        @media print {
          @page {
            size: A4;
            margin: 0;
            background: white;
          }
          
          html, body {
            background: white !important;
            margin: 0;
            padding: 0;
          }
          
          #section-to-print {
            background: white !important;
          }
          
          .resume-preview {
            background: white !important;
          }
          
          .resume-paper {
            width: var(--a4-print-width) !important;
            max-width: var(--a4-print-width) !important;
            min-height: var(--a4-print-height) !important;
            margin: 0 auto !important;
            padding: var(--resume-padding-vertical) var(--resume-padding-horizontal) !important;
            box-shadow: none !important;
            background: white !important;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
          
          .page-break-before {
            page-break-before: always !important;
          }
          
          .keep-together {
            page-break-inside: avoid !important;
          }
          
          .professional-education-item {
            page-break-inside: avoid !important;
          }
        }
      `}</style>
    </div>
  );
};

export default MarketingSpecialistResumePreview;
